/**
 * custom.js
 *
 * Non-animation custom JavaScript for the VeSync project.
 * Place generic custom JS utilities and Custom Element classes here.
 *
 * For all animation-related code (scroll-triggered, IntersectionObserver,
 * requestAnimationFrame, V[Name]Animate classes), use v-animation.js instead.
 */

/**
 * VTabSwitcher
 *
 * Generic tab-switching utility. Handles event binding, panel/trigger
 * class toggling, and auto/scroll modes. Zero DOM knowledge beyond
 * the provided triggers and panels lists.
 *
 * Usage:
 *   const switcher = new VTabSwitcher({ triggers, panels, mode, interval, onActivate, scrollContainer });
 *   switcher.activate(2);
 *   switcher.stop();
 *   switcher.destroy();
 */
class VTabSwitcher {
  /**
   * @param {object} opts
   * @param {NodeList|Element[]} opts.triggers         - Clickable trigger elements
   * @param {NodeList|Element[]} opts.panels           - Tab panel elements
   * @param {'click'|'hover'|'auto'|'scroll'} opts.mode
   * @param {number} [opts.interval=4000]              - Auto-play interval in ms
   * @param {function} [opts.onActivate]               - Callback(newIndex, oldIndex)
   * @param {function} [opts.onScrollProgress]         - Callback(rawIndex) called on every scroll frame in scroll mode
   * @param {number}   [opts.switchThreshold=0.62]        - rawIndex fraction to trigger panel switch (0–1)
   * @param {object}   [opts.segmentSwitchThresholds]     - Per-forward-segment threshold overrides keyed by from-index
   * @param {object}   [opts.segmentReverseThresholds]    - Per-reverse-segment threshold overrides keyed by from-index
   * @param {boolean}  [opts.snapToSwitchThreshold=false] - Snap wheel/touch gestures to switch thresholds instead of whole panel indexes
   * @param {number}   [opts.snapThresholdOffset=0]       - Small offset applied when snapping to thresholds to avoid browser rounding stalls
   * @param {number}   [opts.vhPerSegment=100]         - Viewport-heights of scroll distance per panel segment
   * @param {Element} [opts.scrollContainer]           - Element to observe for scroll-hijack mode
   * @param {Element} [opts.centerTarget]              - Element to center in viewport when locking scroll (defaults to scrollContainer)
   * @param {boolean} [opts.disableScrollTriggerOpacity=false] - Skip --_tab-opacity fade while scrolling (Webflow-style discrete tabs)
   */
  constructor({ triggers, panels, mode = 'click', interval = 4000, onActivate, onScrollProgress, switchThreshold, segmentSwitchThresholds, segmentReverseThresholds, snapToSwitchThreshold, snapThresholdOffset, smoothScrollSegments, vhPerSegment, scrollContainer, centerTarget, disableScrollTriggerOpacity } = {}) {
    this._triggers = Array.from(triggers || []);
    this._panels   = Array.from(panels   || []);
    this._mode     = mode;
    this._interval = interval;
    this._onActivate = onActivate || null;
    this._onScrollProgress = onScrollProgress || null;
    this._switchThreshold = switchThreshold ?? 0.62;
    this._segmentSwitchThresholds = segmentSwitchThresholds || {};
    this._segmentReverseThresholds = segmentReverseThresholds || {};
    this._snapToSwitchThreshold = Boolean(snapToSwitchThreshold);
    this._snapThresholdOffset = snapThresholdOffset || 0;
    this._smoothScrollSegments = smoothScrollSegments || null;
    this._vhPerSegment = vhPerSegment || 100;
    this._current  = 0;
    this._timer    = null;
    this._observers = [];
    this._boundHandlers = [];
    this._scrollContainer = scrollContainer || null;
    this._centerTarget    = centerTarget    || null;
    this._disableScrollTriggerOpacity = Boolean(disableScrollTriggerOpacity);

    // scroll-hijack state
    this._scrollLocked   = false;
    this._scrollCooldown = false;

    this._init();
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  activate(index, force = false) {
    const prev = this._current;
    const next = ((index % this._panels.length) + this._panels.length) % this._panels.length;
    if (next === prev && !force) return;

    this._current = next;

    // Toggle panels
    this._panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === next);
    });

    // Toggle triggers
    this._triggers.forEach((trigger, i) => {
      trigger.classList.toggle('is-active', i === next);
      if (trigger.getAttribute('role') === 'tab') {
        trigger.setAttribute('aria-selected', i === next ? 'true' : 'false');
      }
    });

    if (this._onActivate) this._onActivate(next, prev);
  }

  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  restart() {
    this.stop();
    if (this._mode === 'auto') this._startAuto();
  }

  /**
   * Derive the active panel index from the current window scroll position.
   *
   * @returns {number} Panel index (0-based) implied by scroll progress.
   * @remarks Only meaningful in `scroll` mode after `_bindScroll` has run.
   */
  getActiveIndexFromScroll() {
    if (this._mode !== 'scroll' || !this._scrollTrack) return this._current;

    const track = this._scrollTrack;
    const segmentCount = this._panels.length;
    const maxIndex = Math.max(segmentCount - 1, 0);
    const rect = track.getBoundingClientRect();
    const scrollRange = rect.height - window.innerHeight;
    if (scrollRange <= 0) return 0;

    const progress = Math.max(0, Math.min(1, -rect.top / scrollRange));
    const rawIndex = maxIndex > 0 ? progress * maxIndex : 0;
    let nextIndex = 0;

    while (nextIndex < maxIndex && rawIndex >= nextIndex + this._getForwardSwitchThreshold(nextIndex)) {
      nextIndex += 1;
    }
    while (nextIndex > 0 && rawIndex <= nextIndex - this._getReverseSwitchThreshold(nextIndex)) {
      nextIndex -= 1;
    }

    return nextIndex;
  }

  /**
   * Temporarily block tab switches driven by scroll / wheel / touch.
   * Used after ring-journey → scene hand-off so momentum does not skip tab 1.
   *
   * @param {number} [durationMs=900] Hold duration in milliseconds.
   */
  holdCurrentTab(durationMs = 900) {
    this._tabHoldUntil = Date.now() + durationMs;
  }

  /** @returns {boolean} Whether a tab-hold is currently active. */
  _isTabHoldActive() {
    return Boolean(this._tabHoldUntil && Date.now() < this._tabHoldUntil);
  }

  destroy() {
    this.stop();
    this._unlockScroll();

    if (this._stickyEl) {
      this._stickyEl.style.position = this._origStickyPosition || '';
      this._stickyEl.style.top      = this._origStickyTop || '';
      this._stickyEl = null;
    }
    if (this._scrollSpacer) {
      this._scrollSpacer.remove();
      this._scrollSpacer = null;
    }

    // Remove event listeners
    this._boundHandlers.forEach(({ el, type, fn }) => {
      el.removeEventListener(type, fn);
    });
    this._boundHandlers = [];

    // Disconnect IntersectionObservers
    this._observers.forEach(obs => obs.disconnect());
    this._observers = [];
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  _init() {
    switch (this._mode) {
      case 'hover':
        this._bindHover();
        break;
      case 'auto':
        this._bindClick();
        this._startAuto();
        break;
      case 'scroll':
        this._bindScroll();
        break;
      case 'click':
      default:
        this._bindClick();
        break;
    }
  }

  _on(el, type, fn) {
    el.addEventListener(type, fn);
    this._boundHandlers.push({ el, type, fn });
  }

  _getForwardSwitchThreshold(fromIndex) {
    return this._segmentSwitchThresholds[fromIndex] ?? this._switchThreshold;
  }

  _getReverseSwitchThreshold(fromIndex) {
    return this._segmentReverseThresholds[fromIndex] ?? this._switchThreshold;
  }

  _getPanelSnapRawIndex(index) {
    if (index <= 0) return 0;

    const previousThreshold = this._getForwardSwitchThreshold(index - 1);
    return previousThreshold > 1 ? (index - 1) + previousThreshold : index;
  }

  _getGestureSnapRawIndex(targetIdx, currentIdx, maxIndex) {
    if (!this._snapToSwitchThreshold) return targetIdx;

    const baseRawIndex = targetIdx > currentIdx
      ? currentIdx + this._getForwardSwitchThreshold(currentIdx)
      : this._getPanelSnapRawIndex(targetIdx);
    const directionOffset = targetIdx > currentIdx ? this._snapThresholdOffset : -this._snapThresholdOffset;

    return Math.max(0, Math.min(maxIndex, baseRawIndex + directionOffset));
  }

  _bindClick() {
    this._triggers.forEach((trigger, i) => {
      this._on(trigger, 'click', () => {
        if (this._mode === 'auto') this.stop();
        this.activate(i);
      });
    });
  }

  _bindHover() {
    this._triggers.forEach((trigger, i) => {
      this._on(trigger, 'mouseenter', () => this.activate(i));
    });
  }

  _startAuto() {
    this._timer = setInterval(() => {
      this.activate(this._current + 1);
    }, this._interval);
  }

  /**
   * Scroll-driven sticky mode.
   *
   * Instead of hijacking scroll, this mode extends the section's height
   * via a spacer element and uses position:sticky to keep the visible
   * content pinned in the viewport. Tab switching is driven by scroll
   * progress through the extended area. The native scrollbar remains
   * fully functional and visible at all times.
   */
  _bindScroll() {
    if (!this._panels.length) return;

    const container = this._scrollContainer;
    if (!container) return;

    const segmentCount = this._panels.length;
    const target = this._centerTarget || container;

    // track  = the element whose height is extended (receives the spacer)
    // stickyEl = the element pinned at the top of the viewport while scrolling
    const isSame   = (target === container);
    const track    = isSame ? target.parentElement : target;
    const stickyEl = isSame ? target : target.firstElementChild;

    if (!track || !stickyEl) return;

    this._scrollTrack  = track;
    this._stickyEl     = stickyEl;
    this._origStickyPosition = stickyEl.style.position;
    this._origStickyTop      = stickyEl.style.top;

    stickyEl.style.position = 'sticky';
    stickyEl.style.top      = '0';

    const vhPerSegment = this._vhPerSegment || 100;
    const spacer = document.createElement('div');
    spacer.style.height        = `${segmentCount * vhPerSegment}vh`;
    spacer.style.pointerEvents = 'none';
    spacer.setAttribute('aria-hidden', 'true');
    track.appendChild(spacer);
    this._scrollSpacer = spacer;

    const maxIndex = Math.max(segmentCount - 1, 0);
    const switchThreshold = this._switchThreshold;
    let rafId = null;
    let gestureActivateLocked = false;

    const updateScrollState = () => {
      rafId = null;
      const rect        = track.getBoundingClientRect();
      const scrollRange = rect.height - window.innerHeight;
      if (scrollRange <= 0) return;

      const progress = Math.max(0, Math.min(1, -rect.top / scrollRange));
      const rawIndex = maxIndex > 0 ? progress * maxIndex : 0;
      let nextIndex = this._current;

      this._updateScrollProgressState(rawIndex);

      // When a gesture has already activated a panel directly (non-threshold-snap
      // mode), skip scroll-progress switching until the lock clears to prevent
      // the in-flight smooth scroll from reverting the panel.
      if (!gestureActivateLocked) {
        while (nextIndex < maxIndex && rawIndex >= nextIndex + this._getForwardSwitchThreshold(nextIndex)) {
          nextIndex += 1;
        }

        while (nextIndex > 0 && rawIndex <= nextIndex - this._getReverseSwitchThreshold(nextIndex)) {
          nextIndex -= 1;
        }

        if (this._isTabHoldActive()) {
          nextIndex = this._current;
        }

        if (nextIndex !== this._current) {
          this.activate(nextIndex);
        }
      }
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateScrollState);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    this._boundHandlers.push({ el: window, type: 'scroll', fn: onScroll });

    // ── Wheel snap: one wheel gesture → scroll to next/prev panel position ──
    // Trackpads fire many small-deltaY events per gesture (including inertia).
    // We accumulate deltaY and only trigger a panel switch when the accumulated
    // value exceeds a threshold, then lock out further switches until the
    // gesture settles (no wheel events for a debounce period).
    const SNAP_LOCK_MS = 900;

    let wheelLocked = false;
    let wheelAccum = 0;
    let wheelIdleTimer = null;
    const WHEEL_THRESHOLD = 50;
    const WHEEL_IDLE_MS = 120;

    const resetWheelState = () => {
      wheelAccum = 0;
    };

    const onWheel = (e) => {
      const rect = track.getBoundingClientRect();
      const scrollRange = rect.height - window.innerHeight;
      if (scrollRange <= 0) return;

      const atTop    = rect.top >= 0;
      const atBottom = rect.bottom <= window.innerHeight;

      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) return;

      const atFirstTab = this._current === 0;
      const atLastTab  = this._current === maxIndex;

      if ((e.deltaY < 0 && atFirstTab) || (e.deltaY > 0 && atLastTab)) return;

      if (this._isTabHoldActive() && e.deltaY > 0) return;

      e.preventDefault();

      if (wheelLocked) return;

      if ((wheelAccum > 0 && e.deltaY < 0) || (wheelAccum < 0 && e.deltaY > 0)) {
        wheelAccum = 0;
      }

      wheelAccum += e.deltaY;

      clearTimeout(wheelIdleTimer);
      wheelIdleTimer = setTimeout(resetWheelState, WHEEL_IDLE_MS);

      if (Math.abs(wheelAccum) < WHEEL_THRESHOLD) return;

      const dir = wheelAccum > 0 ? 1 : -1;
      const targetIdx = Math.max(0, Math.min(maxIndex, this._current + dir));

      if (targetIdx === this._current) {
        wheelAccum = 0;
        return;
      }

      const segmentPx = scrollRange / maxIndex;

      // smoothScrollSegments: listed from-indexes use threshold-snap + smooth scroll
      // (content activates via scroll progress). All other segments activate the panel
      // immediately and smooth-scroll to the integer position so the scrollbar
      // animates without delay between scrollbar and content.
      const useThresholdSnap = this._smoothScrollSegments
        ? this._smoothScrollSegments.includes(dir > 0 ? this._current : targetIdx)
        : true;
      const targetRawIndex = useThresholdSnap
        ? this._getGestureSnapRawIndex(targetIdx, this._current, maxIndex)
        : this._getPanelSnapRawIndex(targetIdx);
      const targetScroll = rect.top + window.scrollY + (targetRawIndex * segmentPx);

      wheelLocked = true;
      wheelAccum = 0;

      if (!useThresholdSnap) {
        gestureActivateLocked = true;
        this.activate(targetIdx);
      }
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });

      setTimeout(() => {
        wheelLocked = false;
        gestureActivateLocked = false;
      }, SNAP_LOCK_MS);
    };

    stickyEl.addEventListener('wheel', onWheel, { passive: false });
    this._boundHandlers.push({ el: stickyEl, type: 'wheel', fn: onWheel });

    // ── Touch snap: one swipe gesture → scroll to next/prev panel position ──
    let touchStartY = null;
    let touchLocked = false;
    const TOUCH_THRESHOLD = 40;

    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
      if (touchStartY === null || touchLocked) return;

      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      touchStartY = null;

      if (Math.abs(deltaY) < TOUCH_THRESHOLD) return;

      const rect = track.getBoundingClientRect();
      const scrollRange = rect.height - window.innerHeight;
      if (scrollRange <= 0) return;

      const atTop    = rect.top >= 0;
      const atBottom = rect.bottom <= window.innerHeight;
      if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) return;

      const atFirstTab = this._current === 0;
      const atLastTab  = this._current === maxIndex;
      if ((deltaY < 0 && atFirstTab) || (deltaY > 0 && atLastTab)) return;

      if (this._isTabHoldActive() && deltaY > 0) return;

      const dir = deltaY > 0 ? 1 : -1;
      const targetIdx = Math.max(0, Math.min(maxIndex, this._current + dir));

      if (targetIdx === this._current) return;

      const segmentPx = scrollRange / maxIndex;

      // smoothScrollSegments: listed from-indexes use threshold-snap + smooth scroll
      // (content activates via scroll progress). All other segments activate the panel
      // immediately and smooth-scroll to the integer position so the scrollbar
      // animates without delay between scrollbar and content.
      const useThresholdSnap = this._smoothScrollSegments
        ? this._smoothScrollSegments.includes(dir > 0 ? this._current : targetIdx)
        : true;
      const targetRawIndex = useThresholdSnap
        ? this._getGestureSnapRawIndex(targetIdx, this._current, maxIndex)
        : this._getPanelSnapRawIndex(targetIdx);
      const targetScroll = rect.top + window.scrollY + (targetRawIndex * segmentPx);

      touchLocked = true;

      if (!useThresholdSnap) {
        gestureActivateLocked = true;
        this.activate(targetIdx);
      }
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });

      setTimeout(() => {
        touchLocked = false;
        gestureActivateLocked = false;
      }, SNAP_LOCK_MS);
    };

    const onTouchCancel = () => { touchStartY = null; };

    stickyEl.addEventListener('touchstart', onTouchStart, { passive: true });
    stickyEl.addEventListener('touchend', onTouchEnd, { passive: true });
    stickyEl.addEventListener('touchcancel', onTouchCancel, { passive: true });
    this._boundHandlers.push({ el: stickyEl, type: 'touchstart', fn: onTouchStart });
    this._boundHandlers.push({ el: stickyEl, type: 'touchend', fn: onTouchEnd });
    this._boundHandlers.push({ el: stickyEl, type: 'touchcancel', fn: onTouchCancel });

    requestAnimationFrame(updateScrollState);
  }

  _updateScrollProgressState(rawIndex) {
    if (this._mode !== 'scroll') return;

    if (!this._disableScrollTriggerOpacity) {
      this._triggers.forEach((trigger, index) => {
        const distance = Math.abs(rawIndex - index);
        const opacity = Math.max(0.3, 1 - distance * 0.42);
        trigger.style.setProperty('--_tab-opacity', opacity.toFixed(3));
      });
    }

    if (this._onScrollProgress) this._onScrollProgress(rawIndex);
  }

  _computeFrozenScrollY(container) {
    const target = this._centerTarget || container;
    const rect   = target.getBoundingClientRect();
    const centeredScrollY = Math.round(
      window.scrollY + rect.top - (window.innerHeight - rect.height) / 2
    );
    return Math.max(0, centeredScrollY);
  }

  _smoothLockScroll(container) {
    if (this._scrollLocked || this._scrollTransitioning) return;
    this._scrollTransitioning = true;

    const targetY = this._computeFrozenScrollY(container);
    const distance = Math.abs(window.scrollY - targetY);

    if (distance < 4) {
      this._scrollTransitioning = false;
      this._lockScroll(container);
      return;
    }

    window.scrollTo({ top: targetY, behavior: 'smooth' });

    const checkArrival = () => {
      if (Math.abs(window.scrollY - targetY) < 2) {
        this._scrollTransitioning = false;
        this._lockScroll(container);
      } else {
        requestAnimationFrame(checkArrival);
      }
    };
    requestAnimationFrame(checkArrival);

    setTimeout(() => {
      if (this._scrollTransitioning) {
        this._scrollTransitioning = false;
        this._lockScroll(container);
      }
    }, 800);
  }

  _lockScroll(container) {
    if (this._scrollLocked) return;
    this._scrollLocked = true;

    this._frozenScrollY = this._computeFrozenScrollY(container);
    this._frozenDocHeight = document.documentElement.scrollHeight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this._scrollbarWidth = scrollbarWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    window.scrollTo(0, this._frozenScrollY);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${this._frozenScrollY}px`;
    document.body.style.width    = '100%';

    this._wheelAccum      = 0;
    this._wheelThreshold  = 30;
  }

  _unlockScroll() {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;

    const scrollY = this._frozenScrollY || 0;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollY);

    this._wheelAccum = 0;
  }

  /**
   * Check whether the page has enough scrollable room beyond the locked section
   * for the section to leave the viewport. Prevents unlock when at page edges.
   */
  _hasRoomToScroll(direction) {
    const scrollY  = this._frozenScrollY || 0;
    if (direction === 'up') return scrollY > 10;
    const docHeight = this._frozenDocHeight || document.documentElement.scrollHeight;
    return scrollY + window.innerHeight < docHeight - 10;
  }

  /**
   * Cooldown between tab switches — prevents rapid-fire skipping.
   * 700 ms gives the CSS transition time to finish before the next switch.
   */
  _startCooldown() {
    this._scrollCooldown = true;
    setTimeout(() => { this._scrollCooldown = false; }, 700);
  }
}

/**
 * Freeze the width of impact-text elements that have a suffix sibling,
 * so the suffix position stays fixed during count-up animation.
 */
(function freezeImpactTextWidth() {
  function freeze() {
    document.querySelectorAll('.impact-text__suffix').forEach(function (suffix) {
      var impactEl = suffix.parentElement && suffix.parentElement.querySelector('impact-text');
      if (impactEl && !impactEl.dataset.widthFrozen) {
        impactEl.style.width = impactEl.offsetWidth + 'px';
        impactEl.dataset.widthFrozen = '1';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', freeze);
  } else {
    freeze();
  }
})();

/** PC sticky-top reference viewport height (px). */
const STICKY_TOP_REFERENCE_VIEWPORT_HEIGHT = 731;

/**
 * Quadratic viewport-scaled sticky `top`.
 * Below reference height: inverse scale. Above: `1 - (ref/vh)²` ease.
 *
 * @param {number} topAtReferenceViewport Offset at reference height (negative pulls up).
 * @param {number} liftAmplitude Lift range above reference at tall viewports.
 * @param {number} [referenceViewportHeight=731] Reference viewport height in px.
 * @returns Rounded pixel value for `style.top`.
 */
function getViewportScaledStickyTopPx(
  topAtReferenceViewport,
  liftAmplitude,
  referenceViewportHeight = STICKY_TOP_REFERENCE_VIEWPORT_HEIGHT,
) {
  const viewportHeight = window.innerHeight;

  if (viewportHeight <= referenceViewportHeight) {
    return Math.round(
      topAtReferenceViewport * (referenceViewportHeight / viewportHeight),
    );
  }

  const viewportRatio = referenceViewportHeight / viewportHeight;
  const easedLift = 1 - viewportRatio * viewportRatio;

  return Math.round(topAtReferenceViewport + liftAmplitude * easedLift);
}

/**
 * Sticky `top` that vertically centers `stickyEl` in the viewport (below header).
 *
 * @param {Element} stickyEl Pinned element (`.v-app-phone-features` section).
 * @returns Rounded pixel value for `style.top`.
 */
function getVerticallyCenteredStickyTopPx(stickyEl) {
  if (!stickyEl) return 0;

  const stickyAreaHeight =
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height'),
    ) || 0;
  const availableHeight = window.innerHeight - stickyAreaHeight;
  const elementHeight = stickyEl.offsetHeight;

  if (elementHeight >= availableHeight) {
    return Math.round(stickyAreaHeight);
  }

  return Math.round(stickyAreaHeight + (availableHeight - elementHeight) / 2);
}
window.getVerticallyCenteredStickyTopPx = getVerticallyCenteredStickyTopPx;

(function () {
  if (!document.body.classList.contains('template-404')) return;

  var header = document.querySelector('.v-404-hero .section-header');
  if (!header) return;

  var prose = header.querySelector('.prose');
  if (!prose) return;

  var logo = document.createElement('a');
  logo.href = '/';
  logo.className = 'v-404-logo';
  logo.setAttribute('aria-label', 'Go to homepage');
  logo.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0795/7187/8125/files/Logo.png?v=1776325283" alt="VeSync" width="151" height="62">';
  prose.insertBefore(logo, prose.firstChild);

  var btn = document.createElement('a');
  btn.href = '/';
  btn.className = 'v-404-home-btn';
  btn.textContent = 'Go back to home';
  header.appendChild(btn);
})();

/**
 * VFooterAccordion
 *
 * Mobile accordion for footer link groups.
 * Toggles aria-expanded on .v-footer__link-toggle buttons;
 * CSS handles the expand/collapse animation via max-height.
 */
(function () {
  const MQ = '(max-width: 699px)';

  function init() {
    document.querySelectorAll('.v-footer__link-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function () {
  var WAITLIST_TEXT_PATTERN = /join\s+(?:the\s+)?waitlist/i;

  function isWaitlistAutoTrigger(el) {
    if (!(el instanceof Element)) return false;
    if (el.tagName !== 'A' && el.tagName !== 'BUTTON') return false;
    var href = el.getAttribute('href');
    if (el.tagName === 'A' && href && href !== '#' && href !== '') return false;
    if (el.tagName === 'BUTTON' && el.type === 'submit') return false;
    var text = (el.textContent || '').trim();
    return WAITLIST_TEXT_PATTERN.test(text);
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-waitlist-trigger]');
    if (!trigger) {
      var candidate = e.target.closest('a, button');
      if (candidate && isWaitlistAutoTrigger(candidate)) {
        trigger = candidate;
      }
    }
    if (!trigger) return;
    e.preventDefault();
    var modal = document.getElementById('v-waitlist-modal');
    if (!modal) return;

    var gtmModule = trigger.getAttribute('data-waitlist-gtm-module');
    if (gtmModule) {
      var form = modal.querySelector('.v-waitlist-modal__form');
      if (form) form.setAttribute('data-v-gtm-module', gtmModule);
    }

    modal.setAttribute('open', '');
  });
})();

/**
 * VHoldLastFrame
 *
 * Pin the final frame of a Shopify-hosted MP4 when `<video-media data-hold-last-frame>`
 * is rendered (i.e. `video_loop` is off in sections/video.liquid). After the video
 * fires `ended`, paint the current frame to an absolutely positioned <canvas> overlay
 * so the picture stays visible regardless of how the browser treats a finished <video>.
 *
 * The class also defensively keeps `video.loop` off — useful if a future caller renders
 * a <video> via Shopify's `video_tag` without conditioning the `loop` parameter, or if
 * BaseMedia replays the video on re-intersection. The render-side in video.liquid already
 * gates `loop`, so this is a belt-and-braces guard for other consumers.
 *
 * The hold state is cleared when the user seeks back (manual replay), so videos with
 * playback controls remain re-playable.
 */
class VHoldLastFrame {
  static SELECTOR = 'video-media[data-hold-last-frame] video';
  static BOUND_FLAG = 'vHoldLastFrameBound';
  static ENDED_FLAG = 'vHoldEnded';
  static REPLAY_THRESHOLD = 0.1;

  constructor() {
    this._setupAll = this._setupAll.bind(this);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setupAll(document));
    } else {
      this._setupAll(document);
    }

    if (window.Shopify && Shopify.designMode) {
      this._observeDesignMode();
    }
  }

  _setupAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(VHoldLastFrame.SELECTOR).forEach((video) => this._bind(video));
  }

  _bind(video) {
    if (video.dataset[VHoldLastFrame.BOUND_FLAG]) return;
    video.dataset[VHoldLastFrame.BOUND_FLAG] = 'true';

    video.loop = false;
    if (video.hasAttribute('loop')) video.removeAttribute('loop');

    video.addEventListener('play', () => this._onPlay(video));
    video.addEventListener('ended', () => this._onEnded(video));
    video.addEventListener('seeking', () => this._onSeeking(video));
  }

  _onPlay(video) {
    if (video.loop) {
      video.loop = false;
      video.removeAttribute('loop');
    }
    if (video.dataset[VHoldLastFrame.ENDED_FLAG] !== 'true') return;

    if (this._isReplayPosition(video)) {
      this._clearHold(video);
      return;
    }

    try { video.pause(); } catch (_) {}
    this._paint(video);
  }

  _onEnded(video) {
    if (video.dataset[VHoldLastFrame.ENDED_FLAG] === 'true') return;
    video.dataset[VHoldLastFrame.ENDED_FLAG] = 'true';

    this._paint(video);

    try { video.pause(); } catch (_) {}
    try {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = Math.max(0, video.duration - 0.04);
      }
    } catch (_) {}
  }

  _onSeeking(video) {
    if (video.dataset[VHoldLastFrame.ENDED_FLAG] !== 'true') return;
    if (this._isReplayPosition(video)) this._clearHold(video);
  }

  _isReplayPosition(video) {
    const duration = Number.isFinite(video.duration) ? video.duration : Infinity;
    return video.currentTime < duration - VHoldLastFrame.REPLAY_THRESHOLD;
  }

  _clearHold(video) {
    delete video.dataset[VHoldLastFrame.ENDED_FLAG];
    const container = video.closest('video-media');
    const canvas = container && container.querySelector('canvas[data-v-last-frame]');
    if (canvas) canvas.style.display = 'none';
  }

  _paint(video) {
    const container = video.closest('video-media');
    if (!container) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    let canvas = container.querySelector('canvas[data-v-last-frame]');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'v-last-frame';
      canvas.setAttribute('data-v-last-frame', '');
      canvas.setAttribute('aria-hidden', 'true');
      container.classList.add('v-last-frame-host');
      container.appendChild(canvas);
    }

    canvas.width = w;
    canvas.height = h;
    canvas.style.display = '';
    try {
      canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    } catch (_) {
      // drawImage on cross-origin video may throw; leave the video paused on its last frame.
    }
  }

  _observeDesignMode() {
    document.addEventListener('shopify:section:load', (event) => {
      const id = event.detail && event.detail.sectionId;
      const el = id ? document.getElementById('shopify-section-' + id) : null;
      this._setupAll(el || document);
    });
  }
}

new VHoldLastFrame();

/**
 * VMegaDropdownAlign
 *
 * Desktop header mega menus (.v-mega-dropdown): center panel on the trigger
 * summary, clamped inside the viewport. Position is relative to .header__wrapper.
 */
class VMegaDropdownAlign {
  constructor() {
    this._viewportMargin = 20;
    this._mqDesktop = window.matchMedia('(min-width: 1150px)');
    this._onViewportChange = this._alignAllOpen.bind(this);
    this._observers = [];

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setup());
    } else {
      this._setup();
    }
  }

  _setup() {
    const section = document.querySelector('.shopify-section--header');
    if (!section) return;

    section.querySelectorAll('details:has(> .v-mega-dropdown)').forEach((details) => {
      details.addEventListener('toggle', () => this._onDetailsToggle(details));

      const observer = new MutationObserver(() => this._onDetailsToggle(details));
      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
      this._observers.push(observer);
    });

    window.addEventListener('resize', this._onViewportChange, { passive: true });
    this._mqDesktop.addEventListener('change', this._onViewportChange);
  }

  _onDetailsToggle(details) {
    if (details.hasAttribute('open') && this._mqDesktop.matches) {
      requestAnimationFrame(() => this._align(details));
    } else {
      this._reset(details);
    }
  }

  _alignAllOpen() {
    if (!this._mqDesktop.matches) return;

    document
      .querySelectorAll('.shopify-section--header details:has(> .v-mega-dropdown)[open]')
      .forEach((details) => this._align(details));
  }

  _reset(details) {
    const menu = details.querySelector('.v-mega-dropdown');
    if (!menu) return;

    menu.style.removeProperty('left');
    menu.removeAttribute('data-v-mega-aligned');
  }

  _align(details) {
    const menu = details.querySelector('.v-mega-dropdown');
    const trigger = details.querySelector('summary');
    const wrapper = details.closest('.header__wrapper');

    if (!menu || !trigger || !wrapper || !this._mqDesktop.matches) return;

    const menuWidth = menu.offsetWidth;
    if (menuWidth === 0) {
      requestAnimationFrame(() => this._align(details));
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const margin = this._viewportMargin;
    const idealViewportLeft = triggerRect.left + triggerRect.width / 2 - menuWidth / 2;
    const clampedViewportLeft = Math.max(
      margin,
      Math.min(idealViewportLeft, window.innerWidth - menuWidth - margin),
    );

    menu.style.left = `${clampedViewportLeft - wrapperRect.left}px`;
    menu.setAttribute('data-v-mega-aligned', '');
  }
}

new VMegaDropdownAlign();

window.VTabSwitcher = VTabSwitcher;
window.getViewportScaledStickyTopPx = getViewportScaledStickyTopPx;

export { VTabSwitcher };
